"use client";

import { useEffect, useRef, useState, type DependencyList } from "react";

/**
 * Tracks whether a horizontal scroller has more content to either side, for fade and arrow affordances.
 * @param deps - Values that change the content width (e.g. the number of columns).
 * @returns A ref for the scroller and whether content is hidden to the left and to the right.
 * @throws Never.
 */
export function useHorizontalOverflow<T extends HTMLElement>(
  deps: DependencyList
): { scrollerRef: React.RefObject<T>; overflowsLeft: boolean; overflowsRight: boolean } {
  // Hooks
  const scrollerRef = useRef<T>(null);

  // States
  const [overflowsLeft, setOverflowsLeft] = useState(false);
  const [overflowsRight, setOverflowsRight] = useState(false);

  // Effects
  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const measure = () => {
      setOverflowsLeft(element.scrollLeft > 1);
      setOverflowsRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    element.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer.disconnect();
      element.removeEventListener("scroll", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { scrollerRef, overflowsLeft, overflowsRight };
}
